import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { AuthContext, BuyerAgreementContext } from 'contexts';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import bookAppointment from '../../../../api/AppointmentAvailabilityController/bookAppointment';
import recordAppointment from '../../../../api/AppointmentController/recordAppointment';
import AppointmentBookingInternalContext, {
  ActionButtonData,
} from '../AppointmentBookingInternalContext';
import BookingTimeSelectContext from './BookingTimeSelect/BookingTimeSelectContext';
import CoopSelectContext from './CoopSelect/CoopSelectContext';

const SubmitAppointmentContext = React.createContext({});

export const SubmitAppointmentContextProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const { auth } = useContext(AuthContext);
  const { hasBuyerAgreed, showBuyerAgreementModal } = useContext(
    BuyerAgreementContext,
  );
  const {
    listing,
    partialAppointmentConfig,
    isAdminWithAccess,
    isListingAgent,
    requiresBuyerAgreement,
    setActionsButtonData: _setActionsButtonData,
  } = useContext(AppointmentBookingInternalContext);
  const {
    appointmentDate,
    appointmentTime,
    duration,
    recordShowingData,
    selectedTimeStatus,
    setIsDurationPopoverOpen,
    setRestrictedTimeData,
  } = useContext(BookingTimeSelectContext);
  const {
    apptType,
    apptNote,
    apptNotify,
    bookFor,
    buyers,
    coop,
    coopAdditionalData,
  } = useContext(CoopSelectContext);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const setActionsButtonData = useCallback(
    async (data: ActionButtonData[]) => {
      const processedData = data.map(d => ({
        ...d,
        onClick: () => {
          setIsDurationPopoverOpen(false);
          d.onClick();
        },
      }));
      _setActionsButtonData(processedData);
    },
    [_setActionsButtonData, setIsDurationPopoverOpen],
  );

  const handleRecordShowing = useCallback(async () => {
    try {
      if (!coop || (appointmentTime ?? -1) < 0 || !coopAdditionalData)
        return null;
      const date = moment(appointmentDate).startOf('date');
      const dateString = date.format('YYYY-MM-DD');
      const start = date.add(appointmentTime, 'minute').toISOString();
      const end = date.add(duration, 'minute').toISOString();
      setIsSubmitting(true);

      if (coopAdditionalData.coopType === 'agent') {
        await recordAppointment(listing!.id, {
          type: apptType!,
          listingId: listing!.id,
          notify: apptNotify,
          recordAppointment: true,
          coop,
          ...(apptNote && { note: apptNote }),
          date: dateString,
          time: start,
          timeEnd: end,
          buyers: buyers.map(b => b.value),
        });
      } else if (
        coopAdditionalData.coopType === 'customAgent' ||
        coopAdditionalData.coopType === 'nonAgent'
      ) {
        await recordAppointment(listing!.id, {
          type: apptType!,
          listingId: listing!.id,
          notify: { text: false, email: false, office: true },
          recordAppointment: true,
          coop: {},
          customContact: {
            firstName: coop.firstName,
            lastName: coop.lastName,
            name: coop.name,
            email: coop.email,
            phone: coop.phone,
            org: {
              name: coopAdditionalData.orgName,
              phone: coopAdditionalData.orgPhone,
              address: coopAdditionalData.orgAddress,
            },
            ...(coopAdditionalData.coopType === 'customAgent'
              ? {
                  customAgentId: coop.id,
                }
              : { isNotAgent: true, nonAgentId: coop.id! }),
          },
          ...(apptNote && { note: apptNote }),
          date: dateString,
          time: start,
          timeEnd: end,
          buyers: [],
        });
      }
      message.success('Showing has been recorded');
      history.push(`/listing/${listing!.id}/view`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Failed to record showing');
    }
  }, [
    history,
    listing,
    appointmentDate,
    appointmentTime,
    duration,
    apptNote,
    apptNotify,
    apptType,
    buyers,
    coop,
    coopAdditionalData,
  ]);

  const handleBookShowing = useCallback(
    async ({
      redirectVariant,
      timeRestrictionsOverridden = false,
      buyerAgreementOveridden = false,
    }: {
      redirectVariant?: 'default' | 'confirm' | 'autoConfirm';
      timeRestrictionsOverridden?: boolean;
      buyerAgreementOveridden?: boolean;
    }) => {
      try {
        if (!coop || (appointmentTime ?? -1) < 0 || !coopAdditionalData)
          return null;
        const date = moment(appointmentDate).startOf('date');
        const dateString = date.format('YYYY-MM-DD');
        const start = date.add(appointmentTime, 'minute').toISOString();
        const end = date.add(duration, 'minute').toISOString();
        setIsSubmitting(true);

        if (
          !timeRestrictionsOverridden &&
          selectedTimeStatus === 'Restricted' &&
          (isAdminWithAccess || isListingAgent)
        ) {
          setRestrictedTimeData({
            isModalOpen: true,
            onCancel: () => setIsSubmitting(false),
            onOk: () =>
              handleBookShowing({
                redirectVariant,
                timeRestrictionsOverridden: true,
                buyerAgreementOveridden,
              }),
          });
          return;
        }

        if (
          !hasBuyerAgreed &&
          !buyerAgreementOveridden &&
          requiresBuyerAgreement &&
          showBuyerAgreementModal
        ) {
          showBuyerAgreementModal({
            onCancel: () => setIsSubmitting(false),
            onOk: () =>
              handleBookShowing({
                redirectVariant,
                timeRestrictionsOverridden,
                buyerAgreementOveridden: true,
              }),
          });
          return;
        }

        let appointment: Awaited<ReturnType<typeof bookAppointment>>;
        if (coopAdditionalData.coopType === 'agent') {
          appointment = await bookAppointment({
            type: apptType!,
            listingId: listing!.id,
            notify: apptNotify,
            recordAppointment: false,
            coop,
            ...(apptNote && { note: apptNote }),
            date: dateString,
            time: start,
            timeEnd: end,
            buyers: buyers.map(b => b.value),
          });
        } else {
          appointment = await bookAppointment({
            type: apptType!,
            listingId: listing!.id,
            notify: { text: false, email: false, office: true },
            recordAppointment: false,
            coop: {},
            customContact: {
              firstName: coop.firstName,
              lastName: coop.lastName,
              name: coop.name,
              email: coop.email,
              phone: coop.phone,
              org: {
                name: coopAdditionalData.orgName,
                phone: coopAdditionalData.orgPhone,
                address: coopAdditionalData.orgAddress,
              },
              ...(coopAdditionalData.coopType === 'customAgent'
                ? {
                    customAgentId: coop.id,
                  }
                : { isNotAgent: true, nonAgentId: coop.id! }),
            },
            ...(apptNote && { note: apptNote }),
            date: dateString,
            time: start,
            timeEnd: end,
            buyers: [],
          });
        }
        message.success('Showing booked');
        if (redirectVariant === 'confirm') {
          history.push(`/appointments/${appointment.id}/action`);
          return;
        }
        if (appointment.coop.id === auth.id || bookFor === 'self') {
          // For confirmation message modal
          const listingOrgHasCovidFeature =
            listing!.org.features.includes('COVID_19');
          const apptInfo = {
            apptId: appointment.id,
            status: appointment.status,
            orgCovidIncluded: listingOrgHasCovidFeature,
            apptCovidRequired: appointment.covid19RequestedAck,
            isAutoConfirm: redirectVariant === 'autoConfirm',
            listingLine1: appointment.listing.line1,
            listingLine2: appointment.listing.line2,
            durationHours: appointment.duration.hours,
            durationMin: appointment.duration.minutes,
            timezone: appointment.listing.timezone,
            startTime: appointment.time,
            endTime: appointment.timeEnd,
          };
          const encoded = btoa(JSON.stringify(apptInfo));
          const search = new URLSearchParams({ apptInfo: encoded });
          history.push(`/my_business/showings/buyer?${search.toString()}`);
          return;
        }
        if (redirectVariant === 'autoConfirm') {
          if (appointment.isPending && isAdminWithAccess) {
            history.push(`/appointments/${appointment.id}/confirm`);
            return;
          }
        }
        history.push(`/listing/${listing!.id}/view`);
      } catch (err) {
        setIsSubmitting(false);
        if (
          err.status === 403 &&
          err.data.message?.includes(
            'Oops, you can’t book a showing on this listing as it has been updated. Please book a showing on the new listing. URL: ',
          )
        ) {
          const hyperlinkURL = err.data.message.split('URL: ')[1];
          message.warning({
            content: (
              <>
                Oops, you can’t book a showing on this listing as it has been
                updated. Please{' '}
                <a href={hyperlinkURL}>book a showing on the new listing.</a>
              </>
            ),
          });
          return;
        }

        errorReporter.error(err);
        message.error('Failed to book showing');
      }
    },
    [
      appointmentDate,
      appointmentTime,
      apptNote,
      apptNotify,
      apptType,
      auth.id,
      bookFor,
      buyers,
      coop,
      coopAdditionalData,
      duration,
      hasBuyerAgreed,
      history,
      isAdminWithAccess,
      isListingAgent,
      listing,
      requiresBuyerAgreement,
      selectedTimeStatus,
      setRestrictedTimeData,
      showBuyerAgreementModal,
    ],
  );

  useEffect(() => {
    const shouldBeDisabled = (appointmentTime ?? -1) < 0 || isSubmitting;
    if (recordShowingData.isRecordShowing) {
      setActionsButtonData([
        {
          variant: 'record',
          onClick: handleRecordShowing,
          disabled: shouldBeDisabled,
        },
      ]);
      return;
    }
    const isRestricted =
      selectedTimeStatus === 'Restricted' &&
      !isAdminWithAccess &&
      !isListingAgent;
    if (isRestricted) {
      setActionsButtonData([
        {
          variant: partialAppointmentConfig!.autoConfirm
            ? 'autoConfirm'
            : 'book',
          onClick: () => null,
          disabled: true,
        },
      ]);
      return;
    }
    if (
      selectedTimeStatus === 'AdvancedAutoconfirm' ||
      partialAppointmentConfig!.autoConfirm
    ) {
      setActionsButtonData([
        {
          variant: 'autoConfirm',
          onClick: () => handleBookShowing({ redirectVariant: 'autoConfirm' }),
          disabled: shouldBeDisabled,
        },
      ]);
      return;
    }
    if (isAdminWithAccess || isListingAgent) {
      setActionsButtonData([
        {
          variant: 'book',
          onClick: () => handleBookShowing({ redirectVariant: 'default' }),
          disabled: shouldBeDisabled,
        },
        {
          variant: 'confirm',
          onClick: () => handleBookShowing({ redirectVariant: 'confirm' }),
          disabled: shouldBeDisabled,
        },
      ]);
      return;
    }
    setActionsButtonData([
      {
        variant: 'book',
        onClick: () => handleBookShowing({ redirectVariant: 'default' }),
        disabled: shouldBeDisabled,
      },
    ]);
  }, [
    appointmentTime,
    isSubmitting,
    isAdminWithAccess,
    isListingAgent,
    handleRecordShowing,
    handleBookShowing,
    recordShowingData.isRecordShowing,
    setActionsButtonData,
    selectedTimeStatus,
    partialAppointmentConfig,
  ]);

  return (
    <SubmitAppointmentContext.Provider value={{}}>
      {children}
    </SubmitAppointmentContext.Provider>
  );
};
export default SubmitAppointmentContext;
